import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Modal } from '@material-ui/core';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import LibraryIcon from '../../assets/footer-library.svg';
import { useStore } from '../../contexts/StoreContext';
import { CustomerDisplay } from '../../store/LessonStore';
import AuthStore from '../../store/AuthStore';
import Api from '../../utils/Api';
import { BranchEntity } from '../@types/mysqlEntities';
import Page from '../base/Page';
import { Routes } from '../const/Routes';
import { colors } from '../const/Styles';
import CustomerNameFilter from './surface/CustomerNameFilter';
import CustomerGridViewer from './display/CustomerGridViewer';
import OutlinedInputText from '../form/OutlinedInputText';
import DominantButton from '../form/DominantButton';

type BranchFilterValue = null | number | 'UNSET';

const BRANCH_OPTION_ALL = { id: null as BranchFilterValue, label: '' };
const BRANCH_OPTION_UNSET = { id: 'UNSET' as const, label: '未設定' };

const PageLayout = styled.section`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`;

const SearchBoxWrapper = styled.div`
    display: flex;
    align-items: center;
    padding: 10px;
    gap: 5px;
    width: 100%;
    box-sizing: border-box;
    border-bottom: 4px solid ${colors.gray};
    flex-shrink: 0;
    height: fit-content;
    min-height: fit-content;
    max-height: fit-content;
    overflow: hidden;
`;

const BranchSelect = styled.div`
    min-width: 200px;
`;

const SearchInput = styled(OutlinedInputText)`
    flex: 1;
    min-width: 0;
`;

const SearchButton = styled(DominantButton)`
    padding: 8px 16px;
    min-width: auto;
    font-size: 14px;
    background-color: #0067B2 !important;
    
    &:hover {
        background-color: #0067B2 !important;
    }
`;

const CustomerSelectWrapper = styled.section`
    grid-column: 1 / 3;
    overflow: hidden;
    border-top: 4px solid ${colors.gray};
    display: flex;
    width: 100%;
    flex: 1;
`;

const ModalView = styled(Modal)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ModalBox = styled.div`
    width: 466px;
    height: 233px;
    opacity: 0.8;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.white};
    font-size: 25px;
`;

interface LocationState {
    success: boolean;
    message: string;
}

const CustomerList: React.FC = () => {
    const [filteredCustomers, setFilteredCustomers] = useState<
        CustomerDisplay[]
    >([]);
    const [baseCustomers, setBaseCustomers] = useState<CustomerDisplay[]>([]);
    const [searchText, setSearchText] = useState('');
    const [branches, setBranches] = useState<BranchEntity[]>([]);
    const [branchFilter, setBranchFilter] = useState<BranchFilterValue>(null);
    const location = useLocation<LocationState>();

    const { lessonStore } = useStore();
    const { allCustomers } = lessonStore;

    const branchOptions = useMemo(() => {
        const list = branches.map((b) => ({ id: b.id as BranchFilterValue, label: b.name }));
        return [BRANCH_OPTION_ALL, ...list, BRANCH_OPTION_UNSET];
    }, [branches]);

    const currentBranchOption = useMemo(
        () =>
            branchOptions.find((o) => o.id === branchFilter) ?? BRANCH_OPTION_ALL,
        [branchOptions, branchFilter],
    );

    const applySearchFilter = useCallback(
        (customers: CustomerDisplay[], keyword: string): CustomerDisplay[] => {
            const normalized = keyword.trim().toLowerCase();
            if (!normalized) {
                return customers;
            }
            return customers.filter((customer) => {
                const fullName = (customer.name_sei + customer.name_mei).toLowerCase();
                const nameMatch = fullName.includes(normalized);
                const fullNameKana = (customer.name_sei_kana + customer.name_mei_kana).toLowerCase();
                const nameKanaMatch = fullNameKana.includes(normalized);
                const memberIdMatch = customer.user_name
                    ? customer.user_name.toLowerCase() === normalized
                    : false;
                return nameMatch || nameKanaMatch || memberIdMatch;
            });
        },
        [],
    );

    const applyAllFilters = useCallback(
        (
            customers: CustomerDisplay[],
            keyword: string,
            selectedBranch: BranchFilterValue,
        ): CustomerDisplay[] => {
            const searchFiltered = applySearchFilter(customers, keyword);
            if (selectedBranch === null) return searchFiltered;
            if (selectedBranch === 'UNSET') {
                return searchFiltered.filter((c) => c.branch_id == null);
            }
            return searchFiltered.filter((c) => c.branch_id === selectedBranch);
        },
        [applySearchFilter],
    );

    useEffect(() => {
        Api.fetchBranches(AuthStore.tempoId).then(setBranches).catch(console.error);
    }, []);

    const handleClickFilter = useCallback((customers: CustomerDisplay[]) => {
        setBaseCustomers(customers);
    }, []);

    useEffect(() => {
        const base = baseCustomers.length > 0 ? baseCustomers : allCustomers;
        setFilteredCustomers(applyAllFilters(base, searchText, branchFilter));
    }, [allCustomers, baseCustomers, searchText, branchFilter, applyAllFilters]);

    const runSearchWithBranch = useCallback(
        (branchOverride?: BranchFilterValue) => {
            const effectiveBranch = branchOverride !== undefined ? branchOverride : branchFilter;
            const base = baseCustomers.length > 0 ? baseCustomers : allCustomers;
            setFilteredCustomers(applyAllFilters(base, searchText, effectiveBranch));
        },
        [searchText, allCustomers, baseCustomers, branchFilter, applyAllFilters],
    );

    const handleSearch = useCallback(() => {
        runSearchWithBranch();
    }, [runSearchWithBranch]);

    const handleBranchChange = useCallback(
        (_: unknown, value: { id: BranchFilterValue; label: string } | null) => {
            const next = value?.id ?? null;
            setBranchFilter(next);
            runSearchWithBranch(next);
        },
        [runSearchWithBranch],
    );

    const filteredCounts = useMemo(() => {
        const counts: Record<string, number> = {
            ALL: applyAllFilters(allCustomers, searchText, branchFilter).length,
        };
        lessonStore.rows.forEach((row) => {
            counts[row] = applyAllFilters(
                lessonStore.customersPerRow(row),
                searchText,
                branchFilter,
            ).length;
        });
        return counts;
    }, [allCustomers, lessonStore, applyAllFilters, searchText, branchFilter]);

    const [open, setOpen] = useState(true);
    const handleClose = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const history = useHistory();
    const handleSelectCustomer = (customer: CustomerDisplay) => {
        history.push(Routes.LIBRARY_DETAIL.replace(':id', `${customer.id}`));
    };

    return (
        <>
            {location.state?.success ? (
                <ModalView open={open} onClose={handleClose}>
                    <ModalBox>{location.state?.message}</ModalBox>
                </ModalView>
            ) : (
                []
            )}
            <Page headerIcon={LibraryIcon ?? ''} title='ライブラリー'>
                <PageLayout>
                    <SearchBoxWrapper>
                        <SearchInput
                            id='customer-search'
                            placeholder='氏名、氏名かな、または会員IDを入力'
                            value={searchText}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                            onKeyPress={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                            fullWidth
                            style={{ width: '100%', flex: 1 }}
                        />
                        <SearchButton onClick={handleSearch}>
                            検索
                        </SearchButton>
                        {branches.length > 0 && (
                            <BranchSelect>
                                <Autocomplete
                                    options={branchOptions}
                                    getOptionLabel={(o: { id: BranchFilterValue; label: string }) => o.label}
                                    value={currentBranchOption}
                                    onChange={handleBranchChange}
                                    renderInput={(params: React.ComponentProps<typeof TextField>) => (
                                        <TextField
                                            {...params}
                                            variant='outlined'
                                            size='small'
                                            placeholder='拠点'
                                            style={{ backgroundColor: '#f1f0f8' }}
                                        />
                                    )}
                                />
                            </BranchSelect>
                        )}
                    </SearchBoxWrapper>
                    <CustomerSelectWrapper>
                        <CustomerNameFilter
                            onClick={handleClickFilter}
                            filteredCounts={filteredCounts}
                        ></CustomerNameFilter>
                        <CustomerGridViewer
                            onSelect={handleSelectCustomer}
                            customers={filteredCustomers.concat()}
                        ></CustomerGridViewer>
                    </CustomerSelectWrapper>
                </PageLayout>
            </Page>
        </>
    );
};

export default observer(CustomerList);
